import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Edit, Trash2, Upload, Users, Mail, Send, Key, Search, Flag, Image, AlertTriangle, DollarSign, Bell, Download, Loader2, Copy, Archive } from 'lucide-react';
import { toast } from 'sonner';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import EventRegistrationsModal from './EventRegistrationsModal';
import WaypointManager from '@/components/WaypointManager';
import EventGalleryManager from '@/components/EventGalleryManager';
import ChallengeEditor from '@/components/ChallengeEditor';
import DiplomaTemplateUploader from '@/components/DiplomaTemplateUploader';
import { CategoryStampsManager, ShareTemplateUploader, MonthlySummaryPanel } from '@/components/FreeModeExtras';
import EventTrashDialog from '@/components/EventTrashDialog';

export default function AdminDashboard({ user }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [registrations, setRegistrations] = useState({});
  const [gpxFile, setGpxFile] = useState(null);
  const [posterFile, setPosterFile] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [termsPdfFile, setTermsPdfFile] = useState(null);
  const [rulesPdfFile, setRulesPdfFile] = useState(null);
  const [privacyPdfFile, setPrivacyPdfFile] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  
  // Tracks state (7 tracks)
  const [tracks, setTracks] = useState([
    { enabled: false, name: '', file: null },
    { enabled: false, name: '', file: null },
    { enabled: false, name: '', file: null },
    { enabled: false, name: '', file: null },
    { enabled: false, name: '', file: null },
    { enabled: false, name: '', file: null },
    { enabled: false, name: '', file: null }
  ]);
  
  // Registrations Modal
  const [showRegistrationsModal, setShowRegistrationsModal] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState(null);
  
  // Gallery Modal
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [galleryEventId, setGalleryEventId] = useState(null);
  const [galleryEventTitle, setGalleryEventTitle] = useState('');
  
  // User management states
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showEditUserDialog, setShowEditUserDialog] = useState(false);
  const [showCreateUserDialog, setShowCreateUserDialog] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  
  // Marketing email states
  const [showMarketingModal, setShowMarketingModal] = useState(false);
  const [marketingEventId, setMarketingEventId] = useState(null);
  const [marketingStats, setMarketingStats] = useState(null);
  const [marketingAudience, setMarketingAudience] = useState('all');
  const [sendingMarketing, setSendingMarketing] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [sendingPasswordReset, setSendingPasswordReset] = useState(null);
  
  // Reminder states
  const [sendingReminder, setSendingReminder] = useState(null); // event id being sent
  const [sendingPaymentReminder, setSendingPaymentReminder] = useState(null); // event id being sent
  
  // Rally Masters state
  const [rallyMasters, setRallyMasters] = useState([]);
  const [loadingRallyMasters, setLoadingRallyMasters] = useState(false);
  const [showAssignRallyMaster, setShowAssignRallyMaster] = useState(false);
  const [rallyMasterEmail, setRallyMasterEmail] = useState('');
  const [rallyMasterEventId, setRallyMasterEventId] = useState('');
  const [assigningRallyMaster, setAssigningRallyMaster] = useState(false);
  
  const [userFormData, setUserFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    is_admin: false,
    password: ''
  });
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    organizer: '',
    route_type: '',
    category: '',
    style: '',
    start_date: '',
    end_date: '',
    opening_time: '',  // Event opening time in HH:MM format
    closing_time: '',  // Event closing time in HH:MM format
    max_participants: '',
    price: 0,
    requires_payment: false,
    requires_documents: true,
    requires_dni: true,
    requires_physical_address: true,
    requires_birth_date: true,
    requires_odometer_photo: false,
    requires_tracking: false,
    target_km: '',
    event_mode: 'route',
    requires_iba_number: false,
    requires_arem_number: false,
    requires_aemotur_number: false,
    requires_tshirt_size: false,
    bank_account_iban: '',
    terms_conditions: '',
    participation_rules: '',
    privacy_policy: '',
    cancellation_days: 7,
    whatsapp_group_url: '',
    waypoints: [],
    // Alert configuration
    alert_deviation_enabled: false,
    alert_deviation_distance_km: 10,
    alert_speed_enabled: false,
    alert_speed_limit_kmh: 120,
    alert_check_interval_minutes: 5
  });

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/admin/events`);
      setEvents(response.data);
    } catch (error) {
      toast.error('Error al cargar eventos');
    } finally {
      setLoading(false);
    }
  };

  const fetchRegistrations = async (eventId) => {
    try {
      const response = await axios.get(`${API}/admin/events/${eventId}/registrations`);
      setRegistrations(prev => ({ ...prev, [eventId]: response.data }));
    } catch (error) {
      toast.error('Error al cargar inscripciones');
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    try {
      if (editingEvent) {
        // Update existing event - include existing tracks to prevent deletion
        const updateData = { ...formData };
        
        // CRITICAL: Preserve existing poster_image if not uploading a new one
        // First check posterFile, then check if editingEvent has a poster
        if (!posterFile) {
          if (editingEvent.poster_image) {
            updateData.poster_image = editingEvent.poster_image;
          }
          // Remove any empty/null poster_image to prevent accidental deletion
          if (!updateData.poster_image || updateData.poster_image === '') {
            delete updateData.poster_image;
          }
        }
        
        // CRITICAL: Preserve existing info_pdf if not uploading a new one
        if (!pdfFile) {
          if (editingEvent.info_pdf) {
            updateData.info_pdf = editingEvent.info_pdf;
          }
          if (!updateData.info_pdf || updateData.info_pdf === '') {
            delete updateData.info_pdf;
          }
        }
        
        // Preserve existing legal PDFs if not uploading new ones
        if (!termsPdfFile) {
          if (editingEvent.terms_pdf) {
            updateData.terms_pdf = editingEvent.terms_pdf;
          }
          if (!updateData.terms_pdf || updateData.terms_pdf === '') {
            delete updateData.terms_pdf;
          }
        }
        if (!rulesPdfFile) {
          if (editingEvent.rules_pdf) {
            updateData.rules_pdf = editingEvent.rules_pdf;
          }
          if (!updateData.rules_pdf || updateData.rules_pdf === '') {
            delete updateData.rules_pdf;
          }
        }
        if (!privacyPdfFile) {
          if (editingEvent.privacy_pdf) {
            updateData.privacy_pdf = editingEvent.privacy_pdf;
          }
          if (!updateData.privacy_pdf || updateData.privacy_pdf === '') {
            delete updateData.privacy_pdf;
          }
        }
        
        // Add existing tracks to the update data so they don't get deleted
        for (let i = 0; i < tracks.length; i++) {
          const track = tracks[i];
          const trackNum = i + 1;
          
          // If track has existing file and no new file, preserve it
          if (track.existingFile && !track.file) {
            updateData[`track${trackNum}_name`] = track.name;
            updateData[`track${trackNum}_file`] = track.existingFile;
          }
          // If track is disabled and had existing file, set to null to delete
          else if (!track.enabled && track.existingFile) {
            updateData[`track${trackNum}_name`] = null;
            updateData[`track${trackNum}_file`] = null;
          }
        }
        
        updateData.target_km = updateData.target_km === '' || updateData.target_km == null ? null : parseFloat(updateData.target_km);
        await axios.put(`${API}/admin/events/${editingEvent.id}`, updateData);
        
        // Upload GPX file if provided
        if (gpxFile) {
          const gpxFormData = new FormData();
          gpxFormData.append('file', gpxFile);
          await axios.post(`${API}/admin/events/${editingEvent.id}/gpx`, gpxFormData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        }
        
        // Upload poster if provided
        if (posterFile) {
          const posterFormData = new FormData();
          posterFormData.append('file', posterFile);
          await axios.post(`${API}/admin/events/${editingEvent.id}/poster`, posterFormData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        }
        
        // Upload PDF if provided
        if (pdfFile) {
          const pdfFormData = new FormData();
          pdfFormData.append('file', pdfFile);
          await axios.post(`${API}/admin/events/${editingEvent.id}/pdf`, pdfFormData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        }
        
        // Handle tracks: upload new ones or delete disabled ones
        for (let i = 0; i < tracks.length; i++) {
          const track = tracks[i];
          const trackNum = i + 1;
          
          // If track is disabled and had an existing file, DELETE it
          if (!track.enabled && track.existingFile) {
            try {
              await axios.delete(`${API}/admin/events/${editingEvent.id}/tracks/${trackNum}`);
            } catch (error) {
              console.error(`Error deleting track ${trackNum}:`, error);
            }
          }
          // If track is enabled and has a NEW file to upload
          else if (track.enabled && track.file && track.file instanceof File) {
            const trackFormData = new FormData();
            trackFormData.append('track_name', track.name || `Track ${i + 1}`);
            trackFormData.append('file', track.file);
            await axios.post(`${API}/admin/events/${editingEvent.id}/tracks/${trackNum}`, trackFormData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
          }
        }
        
        // Upload legal PDFs if provided
        if (termsPdfFile) {
          const formData = new FormData();
          formData.append('file', termsPdfFile);
          await axios.post(`${API}/admin/events/${editingEvent.id}/legal-pdfs/terms`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        }
        if (rulesPdfFile) {
          const formData = new FormData();
          formData.append('file', rulesPdfFile);
          await axios.post(`${API}/admin/events/${editingEvent.id}/legal-pdfs/rules`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        }
        if (privacyPdfFile) {
          const formData = new FormData();
          formData.append('file', privacyPdfFile);
          await axios.post(`${API}/admin/events/${editingEvent.id}/legal-pdfs/privacy`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        }
        
        toast.success('Evento actualizado exitosamente');
      } else {
        // Create new event
        const response = await axios.post(`${API}/admin/events`, { ...formData, target_km: formData.target_km === '' ? null : parseFloat(formData.target_km) });
        const eventId = response.data.id;
        
        // Upload GPX file if provided
        if (gpxFile) {
          const gpxFormData = new FormData();
          gpxFormData.append('file', gpxFile);
          await axios.post(`${API}/admin/events/${eventId}/gpx`, gpxFormData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        }
        
        // Upload poster if provided
        if (posterFile) {
          const posterFormData = new FormData();
          posterFormData.append('file', posterFile);
          await axios.post(`${API}/admin/events/${eventId}/poster`, posterFormData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        }
        
        // Upload PDF if provided
        if (pdfFile) {
          const pdfFormData = new FormData();
          pdfFormData.append('file', pdfFile);
          await axios.post(`${API}/admin/events/${eventId}/pdf`, pdfFormData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        }
        
        // Upload tracks if provided (only if there's a NEW file to upload)
        for (let i = 0; i < tracks.length; i++) {
          const track = tracks[i];
          // Only upload if there's a new file (not just an existing one)
          if (track.enabled && track.file && track.file instanceof File) {
            const trackFormData = new FormData();
            trackFormData.append('track_name', track.name || `Track ${i + 1}`);
            trackFormData.append('file', track.file);
            await axios.post(`${API}/admin/events/${eventId}/tracks/${i + 1}`, trackFormData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
          }
        }
        
        // Upload legal PDFs if provided
        if (termsPdfFile) {
          const formData = new FormData();
          formData.append('file', termsPdfFile);
          await axios.post(`${API}/admin/events/${eventId}/legal-pdfs/terms`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        }
        if (rulesPdfFile) {
          const formData = new FormData();
          formData.append('file', rulesPdfFile);
          await axios.post(`${API}/admin/events/${eventId}/legal-pdfs/rules`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        }
        if (privacyPdfFile) {
          const formData = new FormData();
          formData.append('file', privacyPdfFile);
          await axios.post(`${API}/admin/events/${eventId}/legal-pdfs/privacy`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        }
        
        toast.success('Evento creado exitosamente');
      }
      
      setShowCreateDialog(false);
      setEditingEvent(null);
      fetchEvents();
      setFormData({
        title: '',
        description: '',
        organizer: '',
        route_type: '',
        category: '',
        style: '',
        start_date: '',
        end_date: '',
        opening_time: '',
        closing_time: '',
        max_participants: '',
        price: 0,
        requires_payment: false,
        terms_conditions: '',
        participation_rules: '',
        privacy_policy: '',
        cancellation_days: 7,
        whatsapp_group_url: ''
      });
      setGpxFile(null);
      setPosterFile(null);
      setPdfFile(null);
      setTermsPdfFile(null);
      setRulesPdfFile(null);
      setPrivacyPdfFile(null);
      setTracks([
        { enabled: false, name: '', file: null },
        { enabled: false, name: '', file: null },
        { enabled: false, name: '', file: null },
        { enabled: false, name: '', file: null },
        { enabled: false, name: '', file: null },
        { enabled: false, name: '', file: null },
        { enabled: false, name: '', file: null }
      ]);
    } catch (error) {
      console.error('Error completo:', error);
      console.error('Error response:', error.response?.data);
      const errorMessage = error.response?.data?.detail || error.message || 'Error desconocido';
      toast.error(editingEvent ? `Error al actualizar evento: ${errorMessage}` : `Error al crear evento: ${errorMessage}`);
    }
  };

  const handleEditEvent = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      description: event.description,
      organizer: event.organizer,
      route_type: event.route_type,
      category: event.category,
      style: event.style,
      start_date: event.start_date,
      end_date: event.end_date,
      opening_time: event.opening_time || '',
      closing_time: event.closing_time || '',
      max_participants: event.max_participants,
      price: event.price,
      requires_payment: event.requires_payment,
      requires_documents: event.requires_documents !== undefined ? event.requires_documents : true,
      requires_dni: event.requires_dni !== undefined ? event.requires_dni : false,
      requires_physical_address: event.requires_physical_address !== undefined ? event.requires_physical_address : true,
      requires_birth_date: event.requires_birth_date !== undefined ? event.requires_birth_date : true,
      requires_odometer_photo: event.requires_odometer_photo || false,
      requires_tracking: event.requires_tracking || false,
      target_km: event.target_km ?? '',
      event_mode: event.event_mode || 'route',
      challenges: event.challenges || [],
      requires_iba_number: event.requires_iba_number || false,
      requires_arem_number: event.requires_arem_number || false,
      requires_aemotur_number: event.requires_aemotur_number || false,
      requires_tshirt_size: event.requires_tshirt_size || false,
      bank_account_iban: event.bank_account_iban || '',
      terms_conditions: event.terms_conditions,
      participation_rules: event.participation_rules,
      privacy_policy: event.privacy_policy || '',
      cancellation_days: event.cancellation_days || 7,
      whatsapp_group_url: event.whatsapp_group_url || '',
      waypoints: event.waypoints || [],
      // Alert configuration
      alert_deviation_enabled: event.alert_deviation_enabled || false,
      alert_deviation_distance_km: event.alert_deviation_distance_km || 10,
      alert_speed_enabled: event.alert_speed_enabled || false,
      alert_speed_limit_kmh: event.alert_speed_limit_kmh || 120,
      alert_check_interval_minutes: event.alert_check_interval_minutes || 5
    });
    
    // Load existing tracks
    const loadedTracks = [1, 2, 3, 4, 5, 6, 7].map(num => ({
      enabled: !!event[`track${num}_file`],
      name: event[`track${num}_name`] || '',
      file: null,
      existingFile: event[`track${num}_file`] || null
    }));
    setTracks(loadedTracks);
    
    setShowCreateDialog(true);
  };

  const handleDeleteEvent = async (eventId) => {
    const ev = events.find((e) => e.id === eventId);
    if (!window.confirm(`¿Eliminar el evento "${ev?.title || ''}"? Irá a la papelera y podrás restaurarlo durante 30 días.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/events/${eventId}`);
      toast.success('Evento movido a la papelera (restaurable 30 días)');
      fetchEvents();
    } catch (error) {
      if (error.response?.status === 409) {
        const typed = window.prompt(`${error.response.data.detail}\n\nEscribe ELIMINAR para confirmar:`);
        if (typed !== 'ELIMINAR') { toast.info('Eliminación cancelada'); return; }
        try {
          const r = await axios.delete(`${API}/admin/events/${eventId}`, { params: { force: true } });
          toast.success(`Evento y ${r.data.removed?.registrations || 0} inscripciones movidos a la papelera (restaurable 30 días)`);
          fetchEvents();
        } catch (e2) {
          toast.error(e2.response?.data?.detail || 'Error al eliminar el evento');
        }
        return;
      }
      toast.error(error.response?.data?.detail || 'Error al eliminar el evento');
    }
  };

  const [showTrash, setShowTrash] = useState(false);

  const handleDuplicateEvent = async (event) => {
    if (!window.confirm(`¿Duplicar "${event.title}"? Se copiarán waypoints, retos, sellos, plantillas y configuración (sin inscripciones). La copia quedará como borrador oculto.`)) return;
    try {
      const r = await axios.post(`${API}/admin/events/${event.id}/duplicate`);
      toast.success(`"${r.data.title}" creado como borrador (${r.data.waypoints} waypoints, ${r.data.challenges} retos)`);
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se pudo duplicar el evento');
    }
  };

  const handleCleanOrphans = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/registrations/orphans`);
      if (!data.count) { toast.info('No hay inscripciones huérfanas'); return; }
      const list = data.orphans.slice(0, 8).map((o) => `• ${o.full_name} (${o.email})`).join('\n');
      if (!window.confirm(`${data.count} inscripciones pertenecen a eventos eliminados:\n${list}${data.count > 8 ? '\n…' : ''}\n\n¿Eliminarlas?`)) return;
      const r = await axios.delete(`${API}/admin/registrations/orphans`);
      toast.success(r.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al limpiar inscripciones');
    }
  };

  // Marketing email functions
  const handleOpenMarketingModal = async (eventId) => {
    setMarketingEventId(eventId);
    setMarketingAudience('all');
    setShowMarketingModal(true);
    
    try {
      const response = await axios.get(`${API}/admin/events/${eventId}/marketing-stats`);
      setMarketingStats(response.data);
    } catch (error) {
      console.error('Error loading marketing stats:', error);
      toast.error('Error al cargar estadísticas');
    }
  };

  const handleSendMarketingEmail = async () => {
    if (!marketingEventId) return;
    
    setSendingMarketing(true);
    try {
      const response = await axios.post(`${API}/admin/events/${marketingEventId}/send-marketing-email`, {
        target_audience: marketingAudience
      });
      
      const { sent, failed, total } = response.data;
      if (sent > 0) {
        toast.success(`✅ Email enviado a ${sent} usuarios${failed > 0 ? ` (${failed} fallidos)` : ''}`);
      } else if (failed > 0) {
        toast.error(`Error: No se pudo enviar ningún email (${failed} fallidos)`);
      }
      
      setShowMarketingModal(false);
      setMarketingStats(null);
    } catch (error) {
      console.error('Error sending marketing email:', error);
      toast.error('Error al enviar email de marketing');
    } finally {
      setSendingMarketing(false);
    }
  };

  const handleCloseDialog = () => {
    setShowCreateDialog(false);
    setEditingEvent(null);
    setFormData({
      title: '',
      description: '',
      organizer: '',
      route_type: '',
      category: '',
      style: '',
      start_date: '',
      end_date: '',
      opening_time: '',
      closing_time: '',
      max_participants: '',
      price: 0,
      requires_payment: false,
      requires_documents: true,
      requires_dni: true,
      requires_physical_address: true,
      requires_birth_date: true,
      requires_odometer_photo: false,
      requires_tracking: false,
    target_km: '',
    event_mode: 'route',
      requires_iba_number: false,
      requires_arem_number: false,
      requires_aemotur_number: false,
      requires_tshirt_size: false,
      bank_account_iban: '',
      terms_conditions: '',
      participation_rules: '',
      privacy_policy: '',
      cancellation_days: 7,
      whatsapp_group_url: '',
      waypoints: [],
      // Alert configuration
      alert_deviation_enabled: false,
      alert_deviation_distance_km: 10,
      alert_speed_enabled: false,
      alert_speed_limit_kmh: 120,
      alert_check_interval_minutes: 5
    });
    setGpxFile(null);
    setPosterFile(null);
    setPdfFile(null);
    setTermsPdfFile(null);
    setRulesPdfFile(null);
    setPrivacyPdfFile(null);
    setTracks([
      { enabled: false, name: '', file: null },
      { enabled: false, name: '', file: null },
      { enabled: false, name: '', file: null },
      { enabled: false, name: '', file: null },
      { enabled: false, name: '', file: null },
      { enabled: false, name: '', file: null },
      { enabled: false, name: '', file: null }
    ]);
  };

  // Send reminder to all participants of an event
  const handleSendReminder = async (eventId) => {
    const confirmed = window.confirm(
      '¿Estás seguro de que quieres enviar un recordatorio a TODOS los inscritos de este evento?'
    );
    if (!confirmed) return;

    setSendingReminder(eventId);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/admin/events/${eventId}/send-reminders`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Recordatorios enviados: ${response.data.sent}, Fallidos: ${response.data.failed}`);
    } catch (error) {
      console.error('Error sending reminders:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar recordatorios');
    } finally {
      setSendingReminder(null);
    }
  };

  // Send payment reminder only to participants with pending payment
  const handleSendPaymentReminder = async (eventId) => {
    const confirmed = window.confirm(
      '¿Estás seguro de que quieres enviar recordatorio de PAGO a los inscritos pendientes de pago?'
    );
    if (!confirmed) return;

    setSendingPaymentReminder(eventId);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/admin/events/${eventId}/send-payment-reminder`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.pending_count === 0) {
        toast.info('No hay inscripciones con pago pendiente');
      } else {
        toast.success(`Recordatorios de pago enviados: ${response.data.sent_count}`);
      }
    } catch (error) {
      console.error('Error sending payment reminders:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar recordatorios de pago');
    } finally {
      setSendingPaymentReminder(null);
    }
  };

  const handleUploadGPX = async (eventId, file) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(`${API}/admin/events/${eventId}/gpx`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Archivo GPX subido correctamente');
      fetchEvents();
    } catch (error) {
      toast.error('Error al subir GPX');
    }
  };

  const handleUpdateEventStatus = async (eventId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/events/${eventId}`, 
        { status: newStatus },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      toast.success(`Estado actualizado a ${newStatus}`);
      fetchEvents();
    } catch (error) {
      console.error('Error updating event status:', error);
      toast.error('Error al actualizar el estado del evento');
    }
  };

  // User management functions
  const [downloadingBackup, setDownloadingBackup] = useState(false);
  const handleDownloadBackup = async () => {
    setDownloadingBackup(true);
    try {
      const response = await axios.get(`${API}/admin/backup/excel`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `respaldo_strategos_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Respaldo descargado');
    } catch (error) {
      toast.error('Error al generar el respaldo');
    } finally {
      setDownloadingBackup(false);
    }
  };

  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Error al cargar usuarios');
    } finally {
      setUsersLoading(false);
    }
  };

  // Rally Masters functions
  const fetchRallyMasters = async () => {
    setLoadingRallyMasters(true);
    try {
      const response = await axios.get(`${API}/admin/rally-masters`);
      setRallyMasters(response.data);
    } catch (error) {
      toast.error('Error al cargar Rally Masters');
    } finally {
      setLoadingRallyMasters(false);
    }
  };

  const handleAssignRallyMaster = async () => {
    if (!rallyMasterEmail || !rallyMasterEventId) {
      toast.error('Selecciona un evento e introduce un email');
      return;
    }
    
    setAssigningRallyMaster(true);
    try {
      await axios.post(`${API}/admin/rally-masters?event_id=${rallyMasterEventId}&user_email=${encodeURIComponent(rallyMasterEmail)}`);
      toast.success('Rally Master asignado correctamente');
      setShowAssignRallyMaster(false);
      setRallyMasterEmail('');
      setRallyMasterEventId('');
      fetchRallyMasters();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al asignar Rally Master');
    } finally {
      setAssigningRallyMaster(false);
    }
  };

  const handleRemoveRallyMaster = async (assignmentId) => {
    if (!confirm('¿Estás seguro de eliminar este Rally Master?')) return;
    
    try {
      await axios.delete(`${API}/admin/rally-masters/${assignmentId}`);
      toast.success('Rally Master eliminado');
      fetchRallyMasters();
    } catch (error) {
      toast.error('Error al eliminar Rally Master');
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setUserFormData({
      full_name: user.full_name || '',
      email: user.email || '',
      phone: user.phone || '',
      is_admin: user.is_admin || false
    });
    setShowEditUserDialog(true);
  };

  const handleUpdateUser = async () => {
    try {
      const { password, ...updateData } = userFormData; // Exclude password from update
      await axios.put(`${API}/admin/users/${editingUser.id}`, updateData);
      toast.success('Usuario actualizado correctamente');
      setShowEditUserDialog(false);
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error(error.response?.data?.detail || 'Error al actualizar usuario');
    }
  };

  const handleCreateUser = async () => {
    // Validation
    if (!userFormData.full_name || !userFormData.email || !userFormData.password) {
      toast.error('Nombre, email y contraseña son obligatorios');
      return;
    }
    if (userFormData.password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setCreatingUser(true);
    try {
      await axios.post(`${API}/admin/users`, {
        full_name: userFormData.full_name,
        email: userFormData.email,
        phone: userFormData.phone || '',
        password: userFormData.password,
        is_admin: userFormData.is_admin
      });
      toast.success('Usuario creado correctamente');
      setShowCreateUserDialog(false);
      setUserFormData({ full_name: '', email: '', phone: '', is_admin: false, password: '' });
      fetchUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error(error.response?.data?.detail || 'Error al crear usuario');
    } finally {
      setCreatingUser(false);
    }
  };

  const openCreateUserDialog = () => {
    setUserFormData({ full_name: '', email: '', phone: '', is_admin: false, password: '' });
    setShowCreateUserDialog(true);
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm('¿Estás seguro de eliminar este usuario? Se eliminarán también sus inscripciones.')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success('Usuario eliminado');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar usuario');
    }
  };

  const handleSendPasswordReset = async (userId, userEmail) => {
    if (!confirm(`¿Enviar email de recuperación de contraseña a ${userEmail}?`)) {
      return;
    }
    
    setSendingPasswordReset(userId);
    try {
      const response = await axios.post(`${API}/admin/users/${userId}/send-password-reset`);
      toast.success(`Email de recuperación enviado a ${response.data.email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar email');
    } finally {
      setSendingPasswordReset(null);
    }
  };

  // Filter users based on search query
  const filteredUsers = users.filter(u => {
    if (!userSearchQuery) return true;
    const query = userSearchQuery.toLowerCase();
    return (
      u.full_name?.toLowerCase().includes(query) ||
      u.email?.toLowerCase().includes(query) ||
      u.phone?.toLowerCase().includes(query)
    );
  });

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Cargando...</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-gray-100 py-12 px-4">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-orange-600 to-gray-800 bg-clip-text text-transparent" data-testid="admin-dashboard-title">
              Panel de Administrador
            </h1>
            <p className="text-gray-600 mt-2">Gestiona tus eventos y usuarios</p>
          </div>
          <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={handleDownloadBackup}
            disabled={downloadingBackup}
            className="border-gray-300 h-11"
            data-testid="admin-backup-btn"
          >
            {downloadingBackup ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Descargar respaldo (Excel)
          </Button>
          <Button
            variant="outline"
            onClick={handleCleanOrphans}
            className="border-amber-300 text-amber-800 h-11"
            title="Elimina inscripciones cuyo evento fue borrado"
            data-testid="admin-clean-orphans-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Inscripciones huérfanas
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowTrash(true)}
            className="border-gray-300 h-11"
            data-testid="admin-trash-btn"
          >
            <Archive className="w-4 h-4 mr-2" />
            Papelera
          </Button>
          <EventTrashDialog open={showTrash} onOpenChange={setShowTrash} onChanged={fetchEvents} />
          </div>
        </div>

        {/* Tabs for Events, Users, and Rally Masters */}
        <Tabs defaultValue="events" className="w-full">
          <TabsList className="grid w-full grid-cols-3 max-w-lg">
            <TabsTrigger value="events">Eventos</TabsTrigger>
            <TabsTrigger value="users" onClick={fetchUsers}>Usuarios</TabsTrigger>
            <TabsTrigger value="rally-masters" onClick={fetchRallyMasters}>Rally Masters</TabsTrigger>
          </TabsList>

          {/* Events Tab */}
          <TabsContent value="events" className="space-y-8">
            <div className="flex justify-end">
              <Button 
                onClick={() => setShowCreateDialog(true)}
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
                data-testid="create-event-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Crear Evento
              </Button>
            </div>

        {/* Create/Edit Event Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={handleCloseDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-event-dialog">
            <DialogHeader>
              <DialogTitle>{editingEvent ? 'Editar Evento' : 'Crear Nuevo Evento'}</DialogTitle>
              <DialogDescription>
                {editingEvent ? 'Modifica los detalles del evento' : 'Completa los detalles del evento de mototurismo'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateEvent} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Título *</Label>
                <Input
                  id="title"
                  data-testid="event-title-input"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Descripción *</Label>
                <Textarea
                  id="description"
                  data-testid="event-description-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="organizer">Organizador *</Label>
                <Input
                  id="organizer"
                  data-testid="event-organizer-input"
                  value={formData.organizer}
                  onChange={(e) => setFormData({ ...formData, organizer: e.target.value })}
                  placeholder="Nombre del organizador"
                  required
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="route_type">Tipo de Ruta *</Label>
                  <select
                    id="route_type"
                    data-testid="event-route-type-input"
                    value={formData.route_type}
                    onChange={(e) => setFormData({ ...formData, route_type: e.target.value })}
                    className="w-full h-10 px-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required
                  >
                    <option value="">Selecciona tipo</option>
                    <option value="off_road">Off Road</option>
                    <option value="trail">Trail (Mixto)</option>
                    <option value="on_road">On Road</option>
                    <option value="encuentro">Encuentro</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="category">Categoría *</Label>
                  <select
                    id="category"
                    data-testid="event-category-input"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full h-10 px-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required
                  >
                    <option value="">Selecciona</option>
                    <option value="easy">Fácil</option>
                    <option value="intermediate">Intermedio</option>
                    <option value="expert">Experto</option>
                    <option value="sin_conduccion">Sin Conducción</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="style">Estilo *</Label>
                  <select
                    id="style"
                    data-testid="event-style-input"
                    value={formData.style}
                    onChange={(e) => setFormData({ ...formData, style: e.target.value })}
                    className="w-full h-10 px-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required
                  >
                    <option value="">Selecciona</option>
                    <option value="slowride">Slowride / Mototurismo</option>
                    <option value="insaneride">Insaneride / Resistencia</option>
                    <option value="noride">NoRide / Amistad</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start_date">Fecha de Inicio *</Label>
                  <Input
                    id="start_date"
                    data-testid="event-start-date-input"
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end_date">Fecha de Final *</Label>
                  <Input
                    id="end_date"
                    data-testid="event-end-date-input"
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    min={formData.start_date}
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="opening_time">Hora de Apertura</Label>
                  <Input
                    id="opening_time"
                    data-testid="event-opening-time-input"
                    type="time"
                    value={formData.opening_time}
                    onChange={(e) => setFormData({ ...formData, opening_time: e.target.value })}
                    placeholder="HH:MM"
                  />
                  <p className="text-xs text-gray-500">Hora oficial de salida del evento</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="closing_time">Hora de Cierre</Label>
                  <Input
                    id="closing_time"
                    data-testid="event-closing-time-input"
                    type="time"
                    value={formData.closing_time}
                    onChange={(e) => setFormData({ ...formData, closing_time: e.target.value })}
                    placeholder="HH:MM"
                  />
                  <p className="text-xs text-gray-500">Hora límite para llegar a meta</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="max_participants">Máx. Participantes *</Label>
                  <Input
                    id="max_participants"
                    data-testid="event-participants-input"
                    type="number"
                    value={formData.max_participants}
                    onChange={(e) => setFormData({ ...formData, max_participants: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cancellation_days">Días máx. para cancelar *</Label>
                  <Input
                    id="cancellation_days"
                    data-testid="event-cancellation-days-input"
                    type="number"
                    min="0"
                    value={formData.cancellation_days}
                    onChange={(e) => setFormData({ ...formData, cancellation_days: parseInt(e.target.value) || 0 })}
                    required
                  />
                  <p className="text-xs text-gray-500">Días antes del evento</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="whatsapp_group_url">Enlace de Grupo de WhatsApp</Label>
                  <Input
                    id="whatsapp_group_url"
                    data-testid="event-whatsapp-input"
                    type="url"
                    placeholder="https://chat.whatsapp.com/..."
                    value={formData.whatsapp_group_url}
                    onChange={(e) => setFormData({ ...formData, whatsapp_group_url: e.target.value })}
                  />
                  <p className="text-xs text-gray-500">Link del grupo de WhatsApp para los participantes (opcional)</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gpx_file">Archivo GPX (Track)</Label>
                  <Input
                    id="gpx_file"
                    data-testid="event-gpx-input"
                    type="file"
                    accept=".gpx"
                    onChange={(e) => setGpxFile(e.target.files[0])}
                    className="cursor-pointer"
                  />
                  {gpxFile && (
                    <p className="text-xs text-green-600 mt-1">✓ {gpxFile.name}</p>
                  )}
                  {editingEvent && editingEvent.gpx_file && !gpxFile && (
                    <p className="text-xs text-blue-600 mt-1">✓ Ya cargado</p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="poster_image">Cartel del Evento (Imagen)</Label>
                  <Input
                    id="poster_image"
                    data-testid="event-poster-input"
                    type="file"
                    accept="image/*"
                    onChange={(e) => setPosterFile(e.target.files[0])}
                    className="cursor-pointer"
                  />
                  {posterFile && (
                    <p className="text-xs text-green-600 mt-1">✓ {posterFile.name}</p>
                  )}
                  {editingEvent && editingEvent.poster_image && !posterFile && (
                    <p className="text-xs text-blue-600 mt-1">✓ Ya cargado</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="info_pdf">PDF Informativo</Label>
                  <Input
                    id="info_pdf"
                    data-testid="event-pdf-input"
                    type="file"
                    accept=".pdf"
                    onChange={(e) => setPdfFile(e.target.files[0])}
                    className="cursor-pointer"
                  />
                  {pdfFile && (
                    <p className="text-xs text-green-600 mt-1">✓ {pdfFile.name}</p>
                  )}
                  {editingEvent && editingEvent.info_pdf && !pdfFile && (
                    <p className="text-xs text-blue-600 mt-1">✓ Ya cargado</p>
                  )}
                </div>
              </div>
              
              {/* Tracks GPX Section */}
              <div className="border-t pt-4 mt-4">
                <h3 className="text-lg font-semibold mb-3 text-gray-900">Tracks GPX (Opcional)</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Puedes subir hasta 7 tracks GPX diferentes con nombres personalizables
                </p>
                <div className="space-y-4">
                  {tracks.map((track, index) => (
                    <div key={`track-slot-${index}`} className="p-4 bg-gray-50 rounded-lg space-y-3">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          id={`track_${index}_enabled`}
                          checked={track.enabled}
                          onChange={(e) => {
                            const newTracks = [...tracks];
                            newTracks[index].enabled = e.target.checked;
                            if (!e.target.checked) {
                              newTracks[index].name = '';
                              newTracks[index].file = null;
                            }
                            setTracks(newTracks);
                          }}
                          className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500"
                        />
                        <Label htmlFor={`track_${index}_enabled`} className="font-semibold">
                          Track {index + 1}
                        </Label>
                      </div>
                      
                      {track.enabled && (
                        <div className="grid grid-cols-2 gap-3 ml-7">
                          <div className="space-y-2">
                            <Label htmlFor={`track_${index}_name`}>Nombre del Track</Label>
                            <Input
                              id={`track_${index}_name`}
                              type="text"
                              placeholder={`Track ${index + 1}`}
                              value={track.name}
                              onChange={(e) => {
                                const newTracks = [...tracks];
                                newTracks[index].name = e.target.value;
                                setTracks(newTracks);
                              }}
                              className="text-sm"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor={`track_${index}_file`}>Archivo GPX</Label>
                            <Input
                              id={`track_${index}_file`}
                              type="file"
                              accept=".gpx"
                              onChange={(e) => {
                                const newTracks = [...tracks];
                                newTracks[index].file = e.target.files[0];
                                setTracks(newTracks);
                              }}
                              className="cursor-pointer text-sm"
                            />
                            {track.file && (
                              <p className="text-xs text-green-600">✓ {track.file.name}</p>
                            )}
                            {!track.file && track.existingFile && (
                              <p className="text-xs text-blue-600">✓ Ya cargado</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <Switch
                  id="requires_payment"
                  data-testid="event-payment-switch"
                  checked={formData.requires_payment}
                  onCheckedChange={(checked) => setFormData({ ...formData, requires_payment: checked })}
                />
                <Label htmlFor="requires_payment">Requiere pago</Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Switch
                  id="requires_dni"
                  checked={formData.requires_dni}
                  onCheckedChange={(checked) => setFormData({ ...formData, requires_dni: checked })}
                />
                <Label htmlFor="requires_dni">Requiere DNI/NIE</Label>
              </div>
              <p className="text-sm text-gray-500 -mt-2">
                Si activas esta opción, los usuarios deberán proporcionar su DNI/NIE al inscribirse
              </p>
              
              <div className="flex items-center space-x-2">
                <Switch
                  id="requires_physical_address"
                  checked={formData.requires_physical_address}
                  onCheckedChange={(checked) => setFormData({ ...formData, requires_physical_address: checked })}
                />
                <Label htmlFor="requires_physical_address">Requiere Dirección Física</Label>
              </div>
              <p className="text-sm text-gray-500 -mt-2">
                Si activas esta opción, los usuarios deberán proporcionar su dirección física al inscribirse
              </p>
              
              <div className="flex items-center space-x-2">
                <Switch
                  id="requires_birth_date"
                  checked={formData.requires_birth_date}
                  onCheckedChange={(checked) => setFormData({ ...formData, requires_birth_date: checked })}
                />
                <Label htmlFor="requires_birth_date">Requiere Fecha de Nacimiento</Label>
              </div>
              <p className="text-sm text-gray-500 -mt-2">
                Si activas esta opción, los usuarios deberán proporcionar su fecha de nacimiento al inscribirse
              </p>
              
              <div className="flex items-center space-x-2">
                <Switch
                  id="requires_documents"
                  checked={formData.requires_documents}
                  onCheckedChange={(checked) => setFormData({ ...formData, requires_documents: checked })}
                />
                <Label htmlFor="requires_documents">Requiere documentos de la moto</Label>
              </div>
              <p className="text-sm text-gray-500 -mt-2">
                Si activas esta opción, los usuarios deberán subir permiso de circulación, ficha técnica y seguro al inscribirse
              </p>
              
              <div className="flex items-center space-x-2">
                <Switch
                  id="requires_odometer_photo"
                  checked={formData.requires_odometer_photo}
                  onCheckedChange={(checked) => setFormData({ ...formData, requires_odometer_photo: checked })}
                />
                <Label htmlFor="requires_odometer_photo">Requiere fotografía del cuentakilómetros</Label>
              </div>
              <p className="text-sm text-gray-500 -mt-2">
                Los usuarios podrán registrar hasta 5 motos con marca, modelo, kilómetros y foto del cuentakilómetros
              </p>
              
              <div className="flex items-center space-x-2">
                <Switch
                  id="requires_tracking"
                  checked={formData.requires_tracking}
                  onCheckedChange={(checked) => setFormData({ ...formData, requires_tracking: checked })}
                />
                <Label htmlFor="requires_tracking">Seguimiento de ruta</Label>
              </div>
              <p className="text-sm text-gray-500 -mt-2">
                Los participantes podrán subir fotos de los waypoints para acreditar el cumplimiento de la ruta (durante el evento + 7 días)
              </p>

              {formData.requires_tracking && (
                <div className="p-3 bg-violet-50 rounded-lg border border-violet-200 space-y-2">
                  <Label htmlFor="event_mode" className="text-sm font-semibold">Modo del evento</Label>
                  <select
                    id="event_mode"
                    value={formData.event_mode}
                    onChange={(e) => setFormData({ ...formData, event_mode: e.target.value })}
                    className="w-full h-11 rounded-md border border-gray-300 bg-white px-3 text-sm"
                    data-testid="event-mode-select"
                  >
                    <option value="route">Ruta (track GPX, waypoints en orden, alertas de desvío y velocidad)</option>
                    <option value="free">Libre / Colección de waypoints (ruta propia, sin alertas, acreditación por foto)</option>
                  </select>
                  {formData.event_mode === 'free' && (
                    <p className="text-xs text-violet-800">
                      Modo libre: el piloto elige su ruta y acredita cada waypoint con una foto (validación automática por GPS de la foto a &lt;500 m, si no, la revisa el Rally Master).
                      No se generan alertas de desvío, velocidad ni parada. La <strong>fecha de fin del evento</strong> es la fecha máxima para completar el reto.
                      Importa los waypoints con categoría y puntos desde Excel/CSV/KML en la sección Waypoints.
                    </p>
                  )}
                </div>
              )}

              {formData.requires_tracking && (
                <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                  <Label htmlFor="target_km" className="text-sm">Objetivo de kilómetros (eventos anuales / de temporada)</Label>
                  <Input
                    id="target_km"
                    type="number"
                    min="0"
                    step="50"
                    placeholder="Ej. 5000"
                    value={formData.target_km}
                    onChange={(e) => setFormData({ ...formData, target_km: e.target.value })}
                    className="mt-1 w-40"
                    data-testid="event-target-km-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">Si lo indicas, el piloto verá una barra de progreso con los km acumulados por GPS en este evento. Déjalo vacío en eventos de un día o fin de semana.</p>
                </div>
              )}

              {/* Waypoints Manager - only show if tracking is enabled AND editing existing event */}
              {formData.requires_tracking && editingEvent && (
                <div className="mt-4 p-4 bg-orange-50 rounded-lg border border-orange-200">
                  <WaypointManager
                    eventId={editingEvent.id}
                    waypoints={formData.waypoints}
                    onWaypointsChange={(waypoints) => setFormData({ ...formData, waypoints })}
                  />
                </div>
              )}
              {formData.requires_tracking && !editingEvent && (
                <p className="text-sm text-orange-600 mt-2 bg-orange-50 p-2 rounded">
                  💡 Guarda el evento primero, luego podrás añadir waypoints editando el evento.
                </p>
              )}

              {formData.requires_tracking && formData.event_mode === 'free' && editingEvent && (
                <>
                  <ChallengeEditor
                    eventId={editingEvent.id}
                    waypoints={formData.waypoints}
                    challenges={formData.challenges}
                    onChange={(challenges) => setFormData({ ...formData, challenges })}
                  />
                  <DiplomaTemplateUploader
                    eventId={editingEvent.id}
                    template={editingEvent.diploma_template}
                    templateType={editingEvent.diploma_template_type}
                    textColor={editingEvent.diploma_text_color}
                    onChange={(d) => setEditingEvent({ ...editingEvent, ...d })}
                  />
                  <ShareTemplateUploader eventId={editingEvent.id} template={editingEvent.share_template} onChange={(url) => setEditingEvent({ ...editingEvent, share_template: url })} />
                  <CategoryStampsManager eventId={editingEvent.id} waypoints={formData.waypoints} stamps={editingEvent.category_stamps} onChange={(stamps) => setEditingEvent({ ...editingEvent, category_stamps: stamps })} />
                  <MonthlySummaryPanel eventId={editingEvent.id} />
                </>
              )}

              {/* Pilot Alerts Configuration */}
              <div className="pt-4 border-t">
                <p className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-500" />
                  Alertas de Pilotos (Rally Master)
                </p>
                <p className="text-xs text-gray-500 mb-4">
                  Configura alertas automáticas que se enviarán al Rally Master cuando un piloto se desvíe del track o exceda el límite de velocidad.
                </p>
                
                <div className="space-y-4 bg-red-50 p-4 rounded-lg border border-red-200">
                  {/* Deviation Alert */}
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="alert_deviation_enabled"
                      checked={formData.alert_deviation_enabled}
                      onCheckedChange={(checked) => setFormData({ ...formData, alert_deviation_enabled: checked })}
                    />
                    <Label htmlFor="alert_deviation_enabled">Alertar por desviación del track</Label>
                  </div>
                  
                  {formData.alert_deviation_enabled && (
                    <div className="ml-6 grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="alert_deviation_distance_km" className="text-sm">Distancia de alerta (km)</Label>
                        <Input
                          id="alert_deviation_distance_km"
                          type="number"
                          min="1"
                          max="50"
                          step="0.5"
                          value={formData.alert_deviation_distance_km}
                          onChange={(e) => setFormData({ ...formData, alert_deviation_distance_km: parseFloat(e.target.value) || 10 })}
                          className="mt-1"
                        />
                        <p className="text-xs text-gray-500 mt-1">Alerta si el piloto se aleja más de esta distancia del track oficial</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Speed Alert */}
                  <div className="flex items-center space-x-2 pt-2 border-t border-red-200">
                    <Switch
                      id="alert_speed_enabled"
                      checked={formData.alert_speed_enabled}
                      onCheckedChange={(checked) => setFormData({ ...formData, alert_speed_enabled: checked })}
                    />
                    <Label htmlFor="alert_speed_enabled">Alertar por exceso de velocidad</Label>
                  </div>
                  
                  {formData.alert_speed_enabled && (
                    <div className="ml-6 grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="alert_speed_limit_kmh" className="text-sm">Límite de velocidad (km/h)</Label>
                        <Input
                          id="alert_speed_limit_kmh"
                          type="number"
                          min="30"
                          max="300"
                          step="5"
                          value={formData.alert_speed_limit_kmh}
                          onChange={(e) => setFormData({ ...formData, alert_speed_limit_kmh: parseFloat(e.target.value) || 120 })}
                          className="mt-1"
                        />
                        <p className="text-xs text-gray-500 mt-1">Alerta si el piloto supera esta velocidad</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Check Interval */}
                  {(formData.alert_deviation_enabled || formData.alert_speed_enabled) && (
                    <div className="pt-2 border-t border-red-200">
                      <Label htmlFor="alert_check_interval_minutes" className="text-sm">Intervalo de verificación (minutos)</Label>
                      <Input
                        id="alert_check_interval_minutes"
                        type="number"
                        min="1"
                        max="30"
                        step="1"
                        value={formData.alert_check_interval_minutes}
                        onChange={(e) => setFormData({ ...formData, alert_check_interval_minutes: parseInt(e.target.value) || 5 })}
                        className="mt-1 w-32"
                      />
                      <p className="text-xs text-gray-500 mt-1">Cada cuántos minutos verificar la posición de los pilotos</p>
                    </div>
                  )}
                  
                  {(formData.alert_deviation_enabled || formData.alert_speed_enabled) && (
                    <p className="text-xs text-orange-600 mt-2">
                      ℹ️ Las alertas se mostrarán en el panel del Rally Master y se enviarán por email al piloto y a los Rally Masters asignados.
                    </p>
                  )}
                </div>
              </div>

              {/* Optional Association Numbers */}
              <div className="pt-4 border-t">
                <p className="text-sm font-medium text-gray-700 mb-3">Campos opcionales de inscripción:</p>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="requires_iba_number"
                      checked={formData.requires_iba_number}
                      onCheckedChange={(checked) => setFormData({ ...formData, requires_iba_number: checked })}
                    />
                    <Label htmlFor="requires_iba_number">Número IBA</Label>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="requires_arem_number"
                      checked={formData.requires_arem_number}
                      onCheckedChange={(checked) => setFormData({ ...formData, requires_arem_number: checked })}
                    />
                    <Label htmlFor="requires_arem_number">Número AREM</Label>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="requires_aemotur_number"
                      checked={formData.requires_aemotur_number}
                      onCheckedChange={(checked) => setFormData({ ...formData, requires_aemotur_number: checked })}
                    />
                    <Label htmlFor="requires_aemotur_number">Número AEMOTUR</Label>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="requires_tshirt_size"
                      checked={formData.requires_tshirt_size}
                      onCheckedChange={(checked) => setFormData({ ...formData, requires_tshirt_size: checked })}
                    />
                    <Label htmlFor="requires_tshirt_size">Talla de camiseta</Label>
                  </div>
                </div>
              </div>
              
              {formData.requires_payment && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="price">Precio (€) *</Label>
                    <Input
                      id="price"
                      data-testid="event-price-input"
                      type="number"
                      step="0.01"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
                      required={formData.requires_payment}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bank_account_iban">Cuenta Bancaria (IBAN)</Label>
                    <Input
                      id="bank_account_iban"
                      data-testid="event-iban-input"
                      type="text"
                      placeholder="ES12 3456 7890 1234 5678 9012"
                      value={formData.bank_account_iban}
                      onChange={(e) => setFormData({ ...formData, bank_account_iban: e.target.value })}
                    />
                    <p className="text-sm text-gray-500">
                      Los inscritos recibirán este IBAN por email para realizar la transferencia
                    </p>
                  </div>
                </>
              )}
              <div className="space-y-2">
                <Label htmlFor="terms_conditions">Recomendaciones y Normas *</Label>
                <Textarea
                  id="terms_conditions"
                  data-testid="event-terms-input"
                  value={formData.terms_conditions}
                  onChange={(e) => setFormData({ ...formData, terms_conditions: e.target.value })}
                  rows={5}
                  required
                />
                <div className="mt-2">
                  <Label htmlFor="terms_pdf" className="text-sm text-gray-600">PDF descargable (opcional)</Label>
                  <Input
                    id="terms_pdf"
                    type="file"
                    accept=".pdf"
                    onChange={(e) => setTermsPdfFile(e.target.files[0])}
                    className="cursor-pointer"
                  />
                  {termsPdfFile && <p className="text-xs text-green-600 mt-1">✓ {termsPdfFile.name}</p>}
                  {!termsPdfFile && editingEvent?.terms_pdf && <p className="text-xs text-blue-600 mt-1">✓ PDF ya cargado</p>}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="participation_rules">Bases de Participación *</Label>
                <Textarea
                  id="participation_rules"
                  data-testid="event-rules-input"
                  value={formData.participation_rules}
                  onChange={(e) => setFormData({ ...formData, participation_rules: e.target.value })}
                  rows={5}
                  required
                />
                <div className="mt-2">
                  <Label htmlFor="rules_pdf" className="text-sm text-gray-600">PDF descargable (opcional)</Label>
                  <Input
                    id="rules_pdf"
                    type="file"
                    accept=".pdf"
                    onChange={(e) => setRulesPdfFile(e.target.files[0])}
                    className="cursor-pointer"
                  />
                  {rulesPdfFile && <p className="text-xs text-green-600 mt-1">✓ {rulesPdfFile.name}</p>}
                  {!rulesPdfFile && editingEvent?.rules_pdf && <p className="text-xs text-blue-600 mt-1">✓ PDF ya cargado</p>}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="privacy_policy">Política de Privacidad y Uso de Imagen *</Label>
                <Textarea
                  id="privacy_policy"
                  data-testid="event-privacy-input"
                  value={formData.privacy_policy}
                  onChange={(e) => setFormData({ ...formData, privacy_policy: e.target.value })}
                  rows={5}
                  required
                />
                <div className="mt-2">
                  <Label htmlFor="privacy_pdf" className="text-sm text-gray-600">PDF descargable (opcional)</Label>
                  <Input
                    id="privacy_pdf"
                    type="file"
                    accept=".pdf"
                    onChange={(e) => setPrivacyPdfFile(e.target.files[0])}
                    className="cursor-pointer"
                  />
                  {privacyPdfFile && <p className="text-xs text-green-600 mt-1">✓ {privacyPdfFile.name}</p>}
                  {!privacyPdfFile && editingEvent?.privacy_pdf && <p className="text-xs text-blue-600 mt-1">✓ PDF ya cargado</p>}
                </div>
              </div>

              <Button type="submit" className="w-full" data-testid="submit-event-btn">
                {editingEvent ? 'Actualizar Evento' : 'Crear Evento'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Events List */}
        {events.length === 0 ? (
          <Card className="text-center py-12" data-testid="no-admin-events">
            <CardContent>
              <p className="text-gray-600">No has creado ningún evento aún</p>
            </CardContent>
          </Card>
        ) : (
          <>
          {/* Próximos Eventos - ordenados por fecha más cercana primero */}
          {events.filter(e => e.status !== 'completed').length > 0 && (
            <div className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                Próximos Eventos
              </h2>
              <div className="grid grid-cols-1 gap-6" data-testid="admin-events-list">
                {events
                  .filter(e => e.status !== 'completed')
                  .sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
                  .map((event) => (
              <Card key={event.id} className="border-0 shadow-lg" data-testid={`admin-event-${event.id}`}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-xl" data-testid={`admin-event-title-${event.id}`}>{event.title}</CardTitle>
                      <CardDescription>
                        {event.start_date === event.end_date 
                          ? event.start_date 
                          : `${event.start_date} - ${event.end_date}`}
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          setGalleryEventId(event.id);
                          setGalleryEventTitle(event.title);
                          setShowGalleryModal(true);
                        }}
                        title="Gestionar galería"
                      >
                        <Image className="w-4 h-4 text-purple-500" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleEditEvent(event)}
                        data-testid={`edit-event-${event.id}`}
                        title="Editar evento"
                      >
                        <Edit className="w-4 h-4 text-blue-500" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleDuplicateEvent(event)}
                        data-testid={`duplicate-event-${event.id}`}
                        title="Duplicar evento (copia como borrador)"
                      >
                        <Copy className="w-4 h-4 text-teal-600" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleDeleteEvent(event.id)}
                        data-testid={`delete-event-${event.id}`}
                        title="Eliminar evento"
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex gap-4 flex-wrap">
                    {event.organizer && (
                      <div>
                        <p className="text-sm text-gray-600">Organizador</p>
                        <p className="font-semibold">{event.organizer}</p>
                      </div>
                    )}
                    {event.requires_payment && (
                      <div>
                        <p className="text-sm text-gray-600">Precio</p>
                        <p className="font-semibold">{event.price}€</p>
                      </div>
                    )}
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Estado</p>
                      <Select 
                        value={event.status || 'active'} 
                        onValueChange={(value) => handleUpdateEventStatus(event.id, value)}
                      >
                        <SelectTrigger className="w-[150px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="draft">
                            <span className="flex items-center gap-2">
                              <span className="w-2 h-2 bg-gray-300 rounded-full border border-gray-500"></span>
                              Borrador (oculto)
                            </span>
                          </SelectItem>
                          <SelectItem value="active">
                            <span className="flex items-center gap-2">
                              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                              Activo
                            </span>
                          </SelectItem>
                          <SelectItem value="blocked">
                            <span className="flex items-center gap-2">
                              <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                              Bloqueado
                            </span>
                          </SelectItem>
                          <SelectItem value="completed">
                            <span className="flex items-center gap-2">
                              <span className="w-2 h-2 bg-gray-500 rounded-full"></span>
                              Realizado
                            </span>
                          </SelectItem>
                          <SelectItem value="cancelled">
                            <span className="flex items-center gap-2">
                              <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                              Cancelado
                            </span>
                          </SelectItem>
                          <SelectItem value="archived">
                            <span className="flex items-center gap-2">
                              <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                              Archivado
                            </span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Tracks GPX</p>
                      {(() => {
                        const trackCount = [1, 2, 3, 4, 5, 6, 7].filter(num => event[`track${num}_file`]).length;
                        return trackCount > 0 ? (
                          <Badge className="bg-blue-100 text-blue-700">{trackCount} {trackCount === 1 ? 'track' : 'tracks'}</Badge>
                        ) : (
                          <Badge className="bg-gray-100 text-gray-700">Sin tracks</Badge>
                        );
                      })()}
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Archivo PDF</p>
                      {event.info_pdf ? (
                        <Badge className="bg-blue-100 text-blue-700">✓ Cargado</Badge>
                      ) : (
                        <Badge className="bg-gray-100 text-gray-700">Sin archivo</Badge>
                      )}
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Archivo Foto</p>
                      {event.poster_image ? (
                        <Badge className="bg-blue-100 text-blue-700">✓ Cargado</Badge>
                      ) : (
                        <Badge className="bg-gray-100 text-gray-700">Sin archivo</Badge>
                      )}
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Inscritos / Máximo</p>
                      <p className="font-semibold">
                        <span className="text-blue-600">{event.registered_count || 0}</span>
                        {" / "}
                        <span className="text-gray-600">{event.max_participants}</span>
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Plazas Libres</p>
                      <p className={`font-semibold ${
                        (event.available_spots || event.max_participants) > 5 
                          ? 'text-green-600' 
                          : (event.available_spots || event.max_participants) > 0 
                          ? 'text-yellow-600' 
                          : 'text-red-600'
                      }`}>
                        {event.available_spots !== undefined ? event.available_spots : event.max_participants}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Cancelación</p>
                      <p className="font-semibold">{event.cancellation_days || 7} días antes</p>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setSelectedEventId(event.id);
                        setShowRegistrationsModal(true);
                      }}
                      className="flex-1 bg-orange-50 hover:bg-orange-100 text-orange-700 border-orange-200"
                      data-testid={`view-registrations-${event.id}`}
                    >
                      <Users className="w-4 h-4 mr-2" />
                      Ver Inscritos
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
              </div>
            </div>
          )}

          {/* Eventos Realizados - ordenados por fecha más reciente primero */}
          {events.filter(e => e.status === 'completed').length > 0 && (
            <div className="mb-8">
              <h2 className="text-xl font-bold text-gray-600 mb-4 flex items-center gap-2">
                <span className="w-3 h-3 bg-gray-400 rounded-full"></span>
                Eventos Realizados
              </h2>
              <div className="grid grid-cols-1 gap-6" data-testid="admin-completed-events-list">
                {events
                  .filter(e => e.status === 'completed')
                  .sort((a, b) => new Date(b.start_date) - new Date(a.start_date))
                  .map((event) => (
              <Card key={event.id} className="border-0 shadow-lg opacity-75" data-testid={`admin-event-${event.id}`}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-xl" data-testid={`admin-event-title-${event.id}`}>{event.title}</CardTitle>
                      <CardDescription>
                        {event.start_date === event.end_date 
                          ? event.start_date 
                          : `${event.start_date} - ${event.end_date}`}
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          setGalleryEventId(event.id);
                          setGalleryEventTitle(event.title);
                          setShowGalleryModal(true);
                        }}
                        title="Gestionar galería"
                      >
                        <Image className="w-4 h-4 text-purple-500" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleEditEvent(event)}
                        data-testid={`edit-event-${event.id}`}
                        title="Editar evento"
                      >
                        <Edit className="w-4 h-4 text-blue-500" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleDuplicateEvent(event)}
                        data-testid={`duplicate-event-${event.id}`}
                        title="Duplicar evento (copia como borrador)"
                      >
                        <Copy className="w-4 h-4 text-teal-600" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleDeleteEvent(event.id)}
                        data-testid={`delete-event-${event.id}`}
                        title="Eliminar evento"
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex gap-4 flex-wrap">
                    {event.organizer && (
                      <span className="text-sm text-gray-600">🏢 {event.organizer}</span>
                    )}
                    <span className="text-sm text-gray-600">👥 {event.registered_count || 0}/{event.max_participants}</span>
                    <span className="text-sm text-gray-600">💰 {event.price > 0 ? `${event.price}€` : 'Gratis'}</span>
                    <span className="text-sm px-2 py-1 bg-gray-100 text-gray-600 rounded">
                      Completado
                    </span>
                  </div>
                  <div className="flex gap-4 flex-wrap text-sm">
                    <span className={event.poster_image ? 'text-green-600' : 'text-red-600'}>
                      Archivo Foto: {event.poster_image ? '✓ Cargado' : '✗ Sin cargar'}
                    </span>
                    <span className={event.info_pdf ? 'text-green-600' : 'text-gray-400'}>
                      PDF Info: {event.info_pdf ? '✓ Cargado' : '○ Opcional'}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      className="flex items-center gap-2"
                      onClick={() => {
                        setSelectedEventId(event.id);
                        fetchRegistrations(event.id);
                        setShowRegistrationsModal(true);
                      }}
                      data-testid={`view-registrations-${event.id}`}
                    >
                      <Users className="w-4 h-4 mr-2" />
                      Ver Inscritos
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
              </div>
            </div>
          )}
          </>
        )}
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-8">
            {usersLoading ? (
              <div className="text-center py-12">Cargando usuarios...</div>
            ) : users.length === 0 ? (
              <Card>
                <CardContent className="text-center py-12">
                  <Users className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-600">No hay usuarios registrados</p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <CardTitle>Usuarios Registrados ({users.length})</CardTitle>
                      <CardDescription>Gestiona los usuarios de la plataforma</CardDescription>
                    </div>
                    <Button 
                      onClick={openCreateUserDialog}
                      className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
                      data-testid="create-user-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Crear Usuario
                    </Button>
                  </div>
                  {/* Search box */}
                  <div className="relative mt-4">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="text"
                      placeholder="Buscar por nombre, email o teléfono..."
                      value={userSearchQuery}
                      onChange={(e) => setUserSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  {userSearchQuery && (
                    <p className="text-sm text-gray-500 mt-2">
                      {filteredUsers.length} resultado(s) para &quot;{userSearchQuery}&quot;
                    </p>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {filteredUsers.map((u) => (
                      <div key={u.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-semibold">{u.full_name}</p>
                            {u.is_admin && (
                              <Badge className="bg-orange-500 text-white">Admin</Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-600">{u.email}</p>
                          {u.phone && <p className="text-sm text-gray-500">Tel: {u.phone}</p>}
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleSendPasswordReset(u.id, u.email)}
                            disabled={sendingPasswordReset === u.id}
                            title="Enviar email para restablecer contraseña"
                          >
                            {sendingPasswordReset === u.id ? (
                              <div className="w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <Key className="w-4 h-4 text-orange-500" />
                            )}
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleEditUser(u)}
                          >
                            <Edit className="w-4 h-4 text-blue-500" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleDeleteUser(u.id)}
                            disabled={u.id === user.id}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Rally Masters Tab */}
          <TabsContent value="rally-masters" className="space-y-8">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">Gestión de Rally Masters</h2>
              <Button 
                onClick={() => setShowAssignRallyMaster(true)}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Asignar Rally Master
              </Button>
            </div>
            
            {loadingRallyMasters ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500 mx-auto"></div>
              </div>
            ) : rallyMasters.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <p className="text-gray-600">No hay Rally Masters asignados</p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="pt-6">
                  <div className="space-y-3">
                    {rallyMasters.map((rm) => (
                      <div key={rm.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                            <Flag className="w-5 h-5 text-purple-600" />
                          </div>
                          <div>
                            <p className="font-medium">{rm.user?.full_name}</p>
                            <p className="text-sm text-gray-600">{rm.user?.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-sm font-medium text-purple-600">{rm.event?.title}</p>
                            <p className="text-xs text-gray-500">
                              Asignado: {new Date(rm.assigned_at).toLocaleDateString('es-ES')}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveRallyMaster(rm.id)}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Assign Rally Master Dialog */}
        <Dialog open={showAssignRallyMaster} onOpenChange={setShowAssignRallyMaster}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Asignar Rally Master</DialogTitle>
              <DialogDescription>Asigna un usuario como Rally Master para un evento específico</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Evento</Label>
                <select
                  value={rallyMasterEventId}
                  onChange={(e) => setRallyMasterEventId(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="">Selecciona un evento</option>
                  {events.filter(e => e.status !== 'archived').map(e => (
                    <option key={e.id} value={e.id}>{e.title}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Email del usuario</Label>
                <Input
                  type="email"
                  placeholder="email@ejemplo.com"
                  value={rallyMasterEmail}
                  onChange={(e) => setRallyMasterEmail(e.target.value)}
                />
                <p className="text-xs text-gray-500">El usuario debe estar registrado en la plataforma</p>
              </div>
              <Button
                onClick={handleAssignRallyMaster}
                disabled={assigningRallyMaster || !rallyMasterEmail || !rallyMasterEventId}
                className="w-full bg-purple-600 hover:bg-purple-700"
              >
                {assigningRallyMaster ? 'Asignando...' : 'Asignar Rally Master'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit User Dialog */}
        <Dialog open={showEditUserDialog} onOpenChange={setShowEditUserDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Editar Usuario</DialogTitle>
              <DialogDescription>Modifica la información del usuario</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit_user_name">Nombre Completo</Label>
                <Input
                  id="edit_user_name"
                  value={userFormData.full_name}
                  onChange={(e) => setUserFormData({ ...userFormData, full_name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_user_email">Email</Label>
                <Input
                  id="edit_user_email"
                  type="email"
                  value={userFormData.email}
                  onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_user_phone">Teléfono</Label>
                <Input
                  id="edit_user_phone"
                  value={userFormData.phone}
                  onChange={(e) => setUserFormData({ ...userFormData, phone: e.target.value })}
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="edit_user_admin"
                  checked={userFormData.is_admin}
                  onCheckedChange={(checked) => setUserFormData({ ...userFormData, is_admin: checked })}
                  disabled={editingUser?.id === user.id}
                />
                <Label htmlFor="edit_user_admin">Administrador</Label>
              </div>
              <Button onClick={handleUpdateUser} className="w-full">
                Guardar Cambios
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Create User Dialog */}
        <Dialog open={showCreateUserDialog} onOpenChange={setShowCreateUserDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Crear Usuario</DialogTitle>
              <DialogDescription>Crea un nuevo usuario en la plataforma</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="create_user_name">Nombre Completo *</Label>
                <Input
                  id="create_user_name"
                  data-testid="create-user-name-input"
                  value={userFormData.full_name}
                  onChange={(e) => setUserFormData({ ...userFormData, full_name: e.target.value })}
                  placeholder="Juan García"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="create_user_email">Email *</Label>
                <Input
                  id="create_user_email"
                  data-testid="create-user-email-input"
                  type="email"
                  value={userFormData.email}
                  onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })}
                  placeholder="usuario@ejemplo.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="create_user_phone">Teléfono</Label>
                <Input
                  id="create_user_phone"
                  data-testid="create-user-phone-input"
                  value={userFormData.phone}
                  onChange={(e) => setUserFormData({ ...userFormData, phone: e.target.value })}
                  placeholder="+34 600 000 000"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="create_user_password">Contraseña *</Label>
                <Input
                  id="create_user_password"
                  data-testid="create-user-password-input"
                  type="password"
                  value={userFormData.password}
                  onChange={(e) => setUserFormData({ ...userFormData, password: e.target.value })}
                  placeholder="Mínimo 6 caracteres"
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="create_user_admin"
                  data-testid="create-user-admin-switch"
                  checked={userFormData.is_admin}
                  onCheckedChange={(checked) => setUserFormData({ ...userFormData, is_admin: checked })}
                />
                <Label htmlFor="create_user_admin">Administrador</Label>
              </div>
              <Button 
                onClick={handleCreateUser} 
                className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
                disabled={creatingUser}
                data-testid="submit-create-user-btn"
              >
                {creatingUser ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Creando...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Crear Usuario
                  </>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Event Registrations Modal */}
      <EventRegistrationsModal
        open={showRegistrationsModal}
        onOpenChange={setShowRegistrationsModal}
        eventId={selectedEventId}
      />

      {/* Marketing Email Modal */}
      <Dialog open={showMarketingModal} onOpenChange={setShowMarketingModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-600" />
              Enviar Email de Marketing
            </DialogTitle>
            <DialogDescription>
              Envía un email promocional sobre este evento a los usuarios registrados en la plataforma.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {marketingStats ? (
              <>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <p className="text-2xl font-bold text-blue-600">{marketingStats.total_users}</p>
                    <p className="text-xs text-gray-600">Total usuarios</p>
                  </div>
                  <div className="bg-green-50 p-3 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">{marketingStats.registered_users}</p>
                    <p className="text-xs text-gray-600">Ya inscritos</p>
                  </div>
                  <div className="bg-orange-50 p-3 rounded-lg">
                    <p className="text-2xl font-bold text-orange-600">{marketingStats.not_registered_users}</p>
                    <p className="text-xs text-gray-600">Sin inscribir</p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <Label className="text-sm font-medium">¿A quién enviar?</Label>
                  <RadioGroup value={marketingAudience} onValueChange={setMarketingAudience}>
                    <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-gray-50">
                      <RadioGroupItem value="all" id="all" />
                      <Label htmlFor="all" className="flex-1 cursor-pointer">
                        <span className="font-medium">Todos los usuarios</span>
                        <span className="text-sm text-gray-500 ml-2">({marketingStats.total_users} emails)</span>
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-gray-50">
                      <RadioGroupItem value="not_registered" id="not_registered" />
                      <Label htmlFor="not_registered" className="flex-1 cursor-pointer">
                        <span className="font-medium">Solo no inscritos</span>
                        <span className="text-sm text-gray-500 ml-2">({marketingStats.not_registered_users} emails)</span>
                      </Label>
                    </div>
                  </RadioGroup>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-sm text-yellow-800">
                    ⚠️ Se enviará un email a <strong>{marketingAudience === 'all' ? marketingStats.total_users : marketingStats.not_registered_users}</strong> usuarios.
                    Esta acción no se puede deshacer.
                  </p>
                </div>
              </>
            ) : (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-sm text-gray-500 mt-2">Cargando estadísticas...</p>
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowMarketingModal(false)}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleSendMarketingEmail}
              disabled={!marketingStats || sendingMarketing}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
            >
              {sendingMarketing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Enviando...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Enviar Email
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Event Gallery Manager Modal */}
      <EventGalleryManager
        open={showGalleryModal}
        onOpenChange={setShowGalleryModal}
        eventId={galleryEventId}
        eventTitle={galleryEventTitle}
      />
    </div>
  );
}
